import React, { useState, useEffect } from 'react';
import { db, auth } from '../../../../firebase';
import { collection, query, onSnapshot, orderBy, addDoc, updateDoc, deleteDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { ManualVideo, MemberModel, BranchModel } from '../../../../models';
import { Play, Plus, Trash2, Edit2, Youtube, X, Layout, Users, FileText, Check, Calendar, Building2 } from 'lucide-react';
import AdminPageHeader from '../../frame/RightPanelHeader';

const ManualRoot: React.FC = () => {
    const [videos, setVideos] = useState<ManualVideo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVideo, setEditingVideo] = useState<ManualVideo | null>(null);

    const [currentUser, setCurrentUser] = useState<MemberModel | null>(null);
    const [branches, setBranches] = useState<BranchModel[]>([]);

    const [form, setForm] = useState({
        title: '',
        youtubeUrl: '',
        description: '',
        targetAudience: ['student'] as ('student' | 'teacher')[],
        branchId: 'all' // Default to 'all' for super_admin, overridden for others
    });

    useEffect(() => {
        // Fetch Current User & Branches (Once)
        const fetchInit = async () => {
            if (auth.currentUser) {
                const userSnap = await getDoc(doc(db, 'members', auth.currentUser.uid));
                if (userSnap.exists()) {
                    const user = userSnap.data() as MemberModel;
                    setCurrentUser(user);

                    // Initial form branchId logic
                    if (user.role !== 'super_admin') {
                        setForm(prev => ({ ...prev, branchId: user.branchId || user.managedBranchId || '' }));
                    }

                    // Fetch Branches if Super Admin
                    if (user.role === 'super_admin') {
                        const branchSnap = await getDocs(collection(db, 'branches'));
                        const branchList: BranchModel[] = [];
                        branchSnap.forEach(b => branchList.push({ id: b.id, ...b.data() } as BranchModel));
                        setBranches(branchList);
                    }
                }
            }
        };
        fetchInit();
    }, []); // Run only once on mount

    useEffect(() => {
        if (!currentUser) return; // Wait for user to be loaded

        const q = query(collection(db, 'manuals'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            const list: ManualVideo[] = [];
            snap.forEach(d => {
                const data = d.data() as ManualVideo;
                // Filter logic: Super Admin sees all, Admin sees own branch + all
                if (currentUser?.role === 'super_admin') {
                    list.push({ id: d.id, ...data });
                } else {
                    const userBranchId = currentUser?.branchId || currentUser?.managedBranchId;
                    if (!data.branchId || data.branchId === 'all' || data.branchId === userBranchId) {
                        list.push({ id: d.id, ...data });
                    }
                }
            });
            setVideos(list);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [currentUser]); // Re-run only when currentUser logic changes

    const getYoutubeId = (url: string) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const videoId = getYoutubeId(form.youtubeUrl);
        if (!videoId) {
            alert('올바른 유튜브 URL을 입력해주세요.');
            return;
        }

        try {
            const data = {
                ...form,
                videoId,
                createdAt: editingVideo ? editingVideo.createdAt : Date.now()
            };

            if (editingVideo) {
                await updateDoc(doc(db, 'manuals', editingVideo.id), data);
            } else {
                await addDoc(collection(db, 'manuals'), data);
            }

            handleCloseModal();
        } catch (error) {
            console.error('Error saving manual:', error);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('정말로 이 매뉴얼을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'manuals', id));
        } catch (error) {
            console.error('Error deleting manual:', error);
        }
    };

    const handleEdit = (video: ManualVideo) => {
        setEditingVideo(video);
        setForm({
            title: video.title,
            youtubeUrl: video.youtubeUrl,
            description: video.description,
            targetAudience: video.targetAudience as ('student' | 'teacher')[],
            branchId: video.branchId || 'all'
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingVideo(null);
        setForm({
            title: '',
            youtubeUrl: '',
            description: '',
            targetAudience: ['student'],
            branchId: currentUser?.role === 'super_admin' ? 'all' : (currentUser?.branchId || currentUser?.managedBranchId || '')
        });
    };

    const toggleAudience = (audience: 'student' | 'teacher') => {
        setForm(prev => ({
            ...prev,
            targetAudience: prev.targetAudience.includes(audience)
                ? prev.targetAudience.filter(a => a !== audience)
                : [...prev.targetAudience, audience]
        }));
    };

    return (
        <div className="h-full flex flex-col bg-slate-50 overflow-hidden">
            <AdminPageHeader
                title="매뉴얼 관리"
                icon={Youtube}
                subtitle="사용자 가이드 및 영상 교육 자료를 관리합니다."
                actions={
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-full text-sm font-black hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                    >
                        <Plus size={18} strokeWidth={3} />
                        새 매뉴얼 등록
                    </button>
                }
            />

            <div className="flex-1 overflow-y-auto p-6 md:p-8">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-64 gap-4">
                        <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                        <p className="text-slate-400 font-bold">로딩 중...</p>
                    </div>
                ) : videos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {videos.map(video => (
                            <div key={video.id} className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all overflow-hidden">
                                <div className="aspect-video relative bg-slate-100">
                                    <img
                                        src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                                        alt={video.title}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-all flex items-center justify-center">
                                        <div className="w-12 h-12 rounded-full bg-white/90 text-indigo-600 flex items-center justify-center transform scale-90 group-hover:scale-100 transition-all shadow-lg">
                                            <Play size={24} className="fill-current ml-0.5" />
                                        </div>
                                    </div>
                                    <div className="absolute top-3 right-3 flex gap-2">
                                        <button
                                            onClick={() => handleEdit(video)}
                                            className="w-9 h-9 rounded-lg bg-white/90 text-slate-600 hover:bg-white hover:text-indigo-600 flex items-center justify-center shadow-lg transition-all"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(video.id)}
                                            className="w-9 h-9 rounded-lg bg-white/90 text-slate-600 hover:bg-rose-500 hover:text-white flex items-center justify-center shadow-lg transition-all"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <div className="flex gap-1.5 mb-3">
                                        {video.targetAudience.map(aud => (
                                            <span key={aud} className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 text-[10px] font-black uppercase border border-slate-100">
                                                {aud === 'student' ? '학생' : '교사'}
                                            </span>
                                        ))}
                                        {currentUser?.role === 'super_admin' && (
                                            <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[10px] font-black uppercase border border-amber-100 ml-auto">
                                                {video.branchId === 'all' || !video.branchId ? '전체 지점' : branches.find(b => b.id === video.branchId)?.name || '지점'}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-bold text-slate-800 line-clamp-1 mb-2">{video.title}</h3>
                                    <p className="text-xs text-slate-500 line-clamp-2 min-h-[32px] mb-4">{video.description}</p>
                                    <div className="flex items-center text-[10px] font-bold text-slate-300 uppercase">
                                        <Calendar size={12} className="mr-1" />
                                        {new Date(video.createdAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-96 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center text-slate-200 mb-6">
                            <Youtube size={40} />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 mb-2">등록된 매뉴얼이 없습니다</h3>
                        <p className="text-slate-400 font-bold">새로운 매뉴얼을 등록하여 가이드를 시작하세요!</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseModal}></div>
                    <div className="relative bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-8 pb-0 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                    <FileText size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-slate-800 tracking-tight">
                                        {editingVideo ? '매뉴얼 수정' : '새 매뉴얼 등록'}
                                    </h2>
                                    <p className="text-xs font-bold text-slate-400">영상 가이드 정보를 입력하세요.</p>
                                </div>
                            </div>
                            <button onClick={handleCloseModal} className="text-slate-300 hover:text-slate-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">매뉴얼 제목</label>
                                    <input
                                        required
                                        type="text"
                                        value={form.title}
                                        onChange={e => setForm({ ...form, title: e.target.value })}
                                        placeholder="매뉴얼 제목을 입력하세요"
                                        className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm font-bold text-slate-700 placeholder:text-slate-300 focus:ring-2 focus:ring-indigo-100 transition-all outline-none"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">유튜브 URL</label>
                                    <input
                                        required
                                        type="url"
                                        value={form.youtubeUrl}
                                        onChange={e => setForm({ ...form, youtubeUrl: e.target.value })}
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm font-bold text-slate-700 placeholder:text-slate-300 focus:ring-2 focus:ring-indigo-100 transition-all outline-none"
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">상세 설명</label>
                                    <textarea
                                        rows={3}
                                        value={form.description}
                                        onChange={e => setForm({ ...form, description: e.target.value })}
                                        placeholder="매뉴얼에 대한 설명을 입력하세요"
                                        className="w-full bg-slate-50 border-none rounded-2xl px-5 py-4 text-sm font-bold text-slate-700 placeholder:text-slate-300 focus:ring-2 focus:ring-indigo-100 transition-all outline-none resize-none"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Users size={14} /> 노출 대상
                                    </label>
                                    <div className="flex gap-2">
                                        {(['student', 'teacher'] as const).map(aud => (
                                            <button
                                                key={aud}
                                                type="button"
                                                onClick={() => toggleAudience(aud)}
                                                className={`flex-1 py-3 rounded-xl text-xs font-bold transition-all border-2 ${form.targetAudience.includes(aud)
                                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100'
                                                    : 'bg-white border-slate-100 text-slate-400 hover:border-indigo-100'
                                                    }`}
                                            >
                                                {aud === 'student' ? '학생' : '교사'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-indigo-600 text-white py-5 rounded-[1.5rem] font-black text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-2"
                            >
                                <Check size={24} />
                                {editingVideo ? '매뉴얼 수정하기' : '새 매뉴얼 등록하기'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualRoot;
